// FEAT-006: the property expense categories, which of them are capital (never allowable against rental income),
// the short help text for categories that are easy to misfile, and the sums the tax and capital reports use.
// Plain CommonJS and identical on the server (services/expenseCategories.js) and in the browser
// (frontend/src/utils/expenseCategories.js); a test checks the two files are byte-for-byte equal.

const CAPITAL_IMPROVEMENTS = 'Capital improvements (CGT)';
const PURCHASE_COSTS = 'Purchase costs (CGT)';
const SALE_COSTS = 'Sale costs (CGT)';
const INITIAL_FURNISHING = 'Initial furnishing (capital, not allowable)';
const REPLACEMENT_FURNITURE = 'Replacement furniture & appliances';
const LEGACY_FURNITURE = 'Furniture & Furnishings'; // the old category: existing rows keep it and stay allowable

// Capital costs are stored per property like any expense but are never part of the UK Tax, Irish Tax or MTD figures.
const CAPITAL_CATEGORIES = [CAPITAL_IMPROVEMENTS, PURCHASE_COSTS, SALE_COSTS, INITIAL_FURNISHING];

// Allowable (revenue) categories first, alphabetical as before, then the capital ones.
const ALLOWABLE_CATEGORIES = [
  'Accountancy & Professional Fees', 'Advertising & Marketing', 'Buildings Insurance', 'Cleaning & Housekeeping',
  'Contents Insurance', 'Council Tax', 'Electrical Works & Testing', 'Fire Safety & Equipment',
  'Gas Safety & Servicing', 'Ground Rent & Service Charges', 'HMO Licence Fees', 'Legal Fees', 'Letting Agent Fees',
  'Mortgage Interest', 'Other Finance Costs', 'Other Allowable Expenses', 'Plumbing & Heating',
  'Property Management Software', REPLACEMENT_FURNITURE, 'Repairs & Maintenance', 'Travel & Motor Expenses',
  'Utilities (Gas, Electric, Water)', 'Void Period Costs',
];

// What a user can choose for a new property expense (and what the AI receipt scan may pick).
const PROPERTY_EXPENSE_CATEGORIES = [...ALLOWABLE_CATEGORIES, ...CAPITAL_CATEGORIES];

// Categories that may already be saved on old rows. They are kept as they are (allowable) but not offered for new entries.
const LEGACY_CATEGORIES = [LEGACY_FURNITURE];

// Finance costs are shown separately in the tax reports (UK: Section 24 treatment).
const FINANCE_COST_CATEGORIES = ['Mortgage Interest', 'Other Finance Costs'];

const CATEGORY_HINTS = {
  [CAPITAL_IMPROVEMENTS]: 'Work that improves the property beyond how it was, such as an extension, loft conversion or a new kitchen where there was none. Not deductible against rent; it counts towards Capital Gains Tax when you sell.',
  [PURCHASE_COSTS]: 'Stamp duty, legal fees on the purchase, survey and searches. Mortgage arrangement fees do not go here: use Other Finance Costs. Counts towards Capital Gains Tax when you sell.',
  [SALE_COSTS]: 'Estate agent and legal fees when you sell the property. Counts towards Capital Gains Tax.',
  [INITIAL_FURNISHING]: 'Furnishing the property for the first time. Not deductible against rent. Replacing worn-out items goes in Replacement furniture & appliances.',
  [REPLACEMENT_FURNITURE]: 'Replacing existing furniture, white goods or appliances like for like. First-time furnishing goes in Initial furnishing.',
  [LEGACY_FURNITURE]: 'Older category. For new entries choose Replacement furniture & appliances or Initial furnishing.',
  'Other Finance Costs': 'Mortgage arrangement and broker fees and early repayment charges. These are finance costs, not purchase costs.',
  'Mortgage Interest': 'The interest part of your mortgage payments only, not the capital repaid.',
  'Repairs & Maintenance': 'Fixing or restoring what is already there. Improvements go in Capital improvements (CGT).',
  'Legal Fees': 'Letting-related legal fees such as tenancy agreements and evictions. Legal fees on buying or selling go in Purchase costs or Sale costs.',
  'Plumbing & Heating': 'Repairs and servicing. A brand-new system is a capital improvement.',
  'Electrical Works & Testing': 'Testing and repairs. A new installation or rewire is a capital improvement.',
  'Fire Safety & Equipment': 'Servicing and replacing equipment. A first-time installation is usually a capital improvement.',
  'Ground Rent & Service Charges': 'Ongoing charges only. Capital works inside a service charge are capital improvements.',
};

const CAPITAL_COSTS_NOTE = "Capital costs aren't deductible against rental income. Record them as capital so they count towards Capital Gains Tax when you sell.";

const isCapitalCategory = (category) => CAPITAL_CATEGORIES.includes(category);
const categoryHint = (category) => CATEGORY_HINTS[category] || '';

// The options for a category drop-down: the current choices, plus the row's own category if it is an older one.
function categoryOptions(current) {
  return current && !PROPERTY_EXPENSE_CATEGORIES.includes(current) ? [...PROPERTY_EXPENSE_CATEGORIES, current] : PROPERTY_EXPENSE_CATEGORIES;
}

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const sumAmounts = (list) => list.reduce((s, e) => s + num(e.amount), 0);

// The one place a property's expenses are split for tax: finance costs, allowable expenses, and capital costs.
// Capital costs are returned for information and are never part of financeCosts or allowable.
function splitTaxExpenses(expenses) {
  const list = expenses || [];
  return {
    financeCosts: sumAmounts(list.filter((e) => FINANCE_COST_CATEGORIES.includes(e.category))),
    allowable: sumAmounts(list.filter((e) => !FINANCE_COST_CATEGORIES.includes(e.category) && !isCapitalCategory(e.category))),
    capital: sumAmounts(list.filter((e) => isCapitalCategory(e.category))),
  };
}

// Capital costs summary per property for the Portfolio Capital report:
// total = purchase price + purchase costs + capital improvements + sale costs.
// Initial furnishing is recorded and shown but is not part of the total. Only paid expenses count.
function capitalCostsSummary(properties, expenses) {
  const paid = (e) => e.paid === true || e.paid === 'true' || e.paid === undefined;
  const byProp = {};
  (expenses || []).filter((e) => isCapitalCategory(e.category) && paid(e)).forEach((e) => {
    const id = String(e.propertyId || e.property_id);
    byProp[id] = byProp[id] || [];
    byProp[id].push(e);
  });
  return (properties || []).map((p) => {
    const mine = byProp[String(p.id)] || [];
    const cd = p.capitalData || p.capital_data || {};
    const purchasePrice = num(p.purchasePrice !== undefined ? p.purchasePrice : cd.purchasePrice);
    const of = (cat) => sumAmounts(mine.filter((e) => e.category === cat));
    const purchaseCosts = of(PURCHASE_COSTS); const capitalImprovements = of(CAPITAL_IMPROVEMENTS);
    const saleCosts = of(SALE_COSTS); const initialFurnishing = of(INITIAL_FURNISHING);
    return {
      id: p.id, address: p.address, currency: p.currency || 'GBP', ownership: p.ownership,
      purchasePrice, purchaseCosts, capitalImprovements, saleCosts, initialFurnishing,
      total: Math.round((purchasePrice + purchaseCosts + capitalImprovements + saleCosts) * 100) / 100,
      hasData: purchasePrice > 0 || mine.length > 0,
    };
  }).filter((r) => r.hasData);
}

module.exports = {
  CAPITAL_IMPROVEMENTS, PURCHASE_COSTS, SALE_COSTS, INITIAL_FURNISHING, REPLACEMENT_FURNITURE, LEGACY_FURNITURE,
  CAPITAL_CATEGORIES, ALLOWABLE_CATEGORIES, PROPERTY_EXPENSE_CATEGORIES, LEGACY_CATEGORIES, FINANCE_COST_CATEGORIES,
  CATEGORY_HINTS, CAPITAL_COSTS_NOTE, isCapitalCategory, categoryHint, categoryOptions, splitTaxExpenses, capitalCostsSummary,
};
